import {
  ClinicianDiagnosis,
  ClinicianLabelField,
  ClinicianLabelValue,
  ClinicianReliability,
  ClinicianSeverity,
  ClinicianTestReason,
} from "types/tests";

export const CLINICIAN_LABEL_OTHER = "OTHER";

export const CLINICIAN_LABEL_FREE_TEXT_MAX_LENGTH = 500;

/** Matches the backend DTO; a longer comment is rejected with a 400. */
export const CLINICIAN_COMMENT_MAX_LENGTH = 2000;

export const CLINICIAN_REVIEW_PROMPT_DELAY_MS = 3000;

export const CLINICIAN_REVIEW_ANSWERED_KEY_PREFIX = "clinician-review-answered:";

export const CLINICIAN_REVIEW_BANNER_Z_INDEX = 1001;
export const CLINICIAN_REVIEW_DROPDOWN_Z_INDEX = CLINICIAN_REVIEW_BANNER_Z_INDEX + 1;

export const CLINICIAN_LABEL_CHOICES: Record<ClinicianLabelField, ClinicianLabelValue[]> = {
  [ClinicianLabelField.Diagnosis]: [
    ClinicianDiagnosis.Normal,
    ClinicianDiagnosis.Glaucoma,
    ClinicianDiagnosis.OcularHypertension,
    ClinicianDiagnosis.Cataract,
    ClinicianDiagnosis.DiabeticRetinopathy,
    ClinicianDiagnosis.MacularDegeneration,
    ClinicianDiagnosis.RetinitisPigmentosa,
    ClinicianDiagnosis.OpticNeuropathy,
    ClinicianDiagnosis.RetinalDetachment,
    ClinicianDiagnosis.NeurologicalPathway,
    ClinicianDiagnosis.Amblyopia,
    ClinicianDiagnosis.MediaOpacity,
    ClinicianDiagnosis.Other,
  ],
  [ClinicianLabelField.Reliability]: [
    ClinicianReliability.Reliable,
    ClinicianReliability.Borderline,
    ClinicianReliability.Unreliable,
    ClinicianReliability.Other,
  ],
  [ClinicianLabelField.TestReason]: [
    ClinicianTestReason.Screening,
    ClinicianTestReason.RoutineFollowUp,
    ClinicianTestReason.GlaucomaSuspect,
    ClinicianTestReason.DiagnosisConfirmation,
    ClinicianTestReason.VisualSymptoms,
    ClinicianTestReason.PreOperative,
    ClinicianTestReason.PostOperative,
    ClinicianTestReason.MedicationMonitoring,
    ClinicianTestReason.NeurologicalReferral,
    ClinicianTestReason.DrivingAssessment,
    ClinicianTestReason.Research,
    ClinicianTestReason.Other,
  ],
};

export const CLINICIAN_SEVERITY_CHOICES: Partial<Record<ClinicianDiagnosis, ClinicianSeverity[]>> =
  {
    [ClinicianDiagnosis.Glaucoma]: [
      ClinicianSeverity.Suspect,
      ClinicianSeverity.Mild,
      ClinicianSeverity.Moderate,
      ClinicianSeverity.Severe,
    ],
    [ClinicianDiagnosis.Cataract]: [
      ClinicianSeverity.Mild,
      ClinicianSeverity.Moderate,
      ClinicianSeverity.Advanced,
    ],
    [ClinicianDiagnosis.DiabeticRetinopathy]: [
      ClinicianSeverity.MildNpdr,
      ClinicianSeverity.ModerateNpdr,
      ClinicianSeverity.SevereNpdr,
      ClinicianSeverity.Pdr,
    ],
    [ClinicianDiagnosis.MacularDegeneration]: [
      ClinicianSeverity.Early,
      ClinicianSeverity.Intermediate,
      ClinicianSeverity.LateAtrophic,
      ClinicianSeverity.LateNeovascular,
    ],
    [ClinicianDiagnosis.RetinitisPigmentosa]: [
      ClinicianSeverity.Early,
      ClinicianSeverity.Moderate,
      ClinicianSeverity.Advanced,
    ],
    [ClinicianDiagnosis.OpticNeuropathy]: [
      ClinicianSeverity.Mild,
      ClinicianSeverity.Moderate,
      ClinicianSeverity.Severe,
    ],
    [ClinicianDiagnosis.NeurologicalPathway]: [
      ClinicianSeverity.Mild,
      ClinicianSeverity.Moderate,
      ClinicianSeverity.Severe,
    ],
  };

export const CLINICIAN_SEVERITY_FIELD = ClinicianLabelField.Diagnosis;

export const severityChoicesFor = (
  value: ClinicianLabelValue | undefined | null,
): ClinicianSeverity[] =>
  value ? (CLINICIAN_SEVERITY_CHOICES[value as ClinicianDiagnosis] ?? []) : [];

export const CLINICIAN_LABEL_CLEARED = "CLEARED";

/* i18n keys resolved dynamically below — listed so the extractor keeps them:
   t("ClinicianLabel.value_CLEARED") t("ClinicianLabel.value_NORMAL") t("ClinicianLabel.value_GLAUCOMA")
   t("ClinicianLabel.value_OCULAR_HYPERTENSION") t("ClinicianLabel.value_CATARACT")
   t("ClinicianLabel.value_DIABETIC_RETINOPATHY") t("ClinicianLabel.value_MACULAR_DEGENERATION")
   t("ClinicianLabel.value_RETINITIS_PIGMENTOSA") t("ClinicianLabel.value_OPTIC_NEUROPATHY")
   t("ClinicianLabel.value_RETINAL_DETACHMENT") t("ClinicianLabel.value_NEUROLOGICAL_PATHWAY")
   t("ClinicianLabel.value_AMBLYOPIA") t("ClinicianLabel.value_MEDIA_OPACITY")
   t("ClinicianLabel.value_RELIABLE") t("ClinicianLabel.value_BORDERLINE")
   t("ClinicianLabel.value_UNRELIABLE") t("ClinicianLabel.value_OTHER")
   t("ClinicianLabel.value_SCREENING") t("ClinicianLabel.value_ROUTINE_FOLLOW_UP")
   t("ClinicianLabel.value_GLAUCOMA_SUSPECT") t("ClinicianLabel.value_DIAGNOSIS_CONFIRMATION")
   t("ClinicianLabel.value_VISUAL_SYMPTOMS") t("ClinicianLabel.value_PRE_OPERATIVE")
   t("ClinicianLabel.value_POST_OPERATIVE") t("ClinicianLabel.value_MEDICATION_MONITORING")
   t("ClinicianLabel.value_NEUROLOGICAL_REFERRAL") t("ClinicianLabel.value_DRIVING_ASSESSMENT")
   t("ClinicianLabel.value_RESEARCH")
   t("ClinicianLabel.severity_SUSPECT") t("ClinicianLabel.severity_MILD")
   t("ClinicianLabel.severity_MODERATE") t("ClinicianLabel.severity_SEVERE")
   t("ClinicianLabel.severity_EARLY") t("ClinicianLabel.severity_INTERMEDIATE")
   t("ClinicianLabel.severity_ADVANCED") t("ClinicianLabel.severity_MILD_NPDR")
   t("ClinicianLabel.severity_MODERATE_NPDR") t("ClinicianLabel.severity_SEVERE_NPDR")
   t("ClinicianLabel.severity_PDR") t("ClinicianLabel.severity_LATE_ATROPHIC")
   t("ClinicianLabel.severity_LATE_NEOVASCULAR")
   t("ClinicianLabel.diagnosis") t("ClinicianLabel.reliability") t("ClinicianLabel.testReason")
   t("ClinicianLabel.freeText_diagnosis") t("ClinicianLabel.freeText_reliability")
   t("ClinicianLabel.freeText_testReason") */

export const clinicianLabelValueKey = (value: ClinicianLabelValue | string): string =>
  "ClinicianLabel.value_" + value;

export const clinicianSeverityKey = (severity: ClinicianSeverity | string): string =>
  "ClinicianLabel.severity_" + severity;

export const clinicianLabelFieldKey = (field: ClinicianLabelField): string =>
  "ClinicianLabel." + field;

export const clinicianLabelFreeTextKey = (field: ClinicianLabelField): string =>
  "ClinicianLabel.freeText_" + field;
